/**
 * Brand Dashboard Layout Component
 * Modern sidebar navigation layout for brand portal
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Package, TrendingUp, Eye, LogOut, User, BookOpen, Bell, X, Check, Menu } from 'lucide-react';
import { useBrand } from '../../context/BrandContext';
import BrandStatusMonitor from '../BrandStatusMonitor';
import '../../assets/css/brand-portal.css';
import { Image } from 'react-bootstrap';

export function DashboardLayout() {
  const navigate = useNavigate();
  const { brand, isAuthenticated, loading, logout, authFetch } = useBrand();
  const [brandHealth, setBrandHealth] = useState(0);
  const [profileData, setProfileData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationRef = useRef(null);

  // Close sidebar on resize to desktop & prevent body scroll when open
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024 && sidebarOpen) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    // Prevent body scroll when mobile sidebar is open
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // Close notification dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await authFetch('/api/brand/notifications/');
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, [isAuthenticated, authFetch]);

  useEffect(() => {
    fetchNotifications();
    // Poll every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      const res = await authFetch('/api/brand/notifications/mark-read/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_id: notificationId }),
      });
      if (res.ok) fetchNotifications();
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const res = await authFetch('/api/brand/notifications/mark-read/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mark_all: true }),
      });
      if (res.ok) fetchNotifications();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/brand-portal/login');
    }
  }, [isAuthenticated, loading, navigate]);

  // Fetch real brand health score and profile data
  useEffect(() => {
    const fetchBrandData = async () => {
      if (!isAuthenticated) return;
      
      try {
        // Fetch both profile and dashboard data for comprehensive health calculation
        const [profileRes, dashboardRes] = await Promise.all([
          authFetch('/api/brand/profile/'),
          authFetch('/api/brand/dashboard/')
        ]);
        
        let profileData = null;
        let dashboardData = null;
        
        if (profileRes.ok) {
          profileData = await profileRes.json();
          setProfileData(profileData);
        }
        
        if (dashboardRes.ok) {
          dashboardData = await dashboardRes.json();
        }
        
        // Calculate comprehensive brand health score based on real metrics
        let score = 0;
        let maxPossibleScore = 100;
        
        // 1. Profile Completeness (25 points max)
        let profileScore = 0;
        if (profileData) {
          if (profileData.logo) profileScore += 6;  // Logo is important
          if (profileData.description && profileData.description.trim().length > 50) profileScore += 6;
          else if (profileData.description && profileData.description.trim().length > 20) profileScore += 3;
          if (profileData.website) profileScore += 4;
          if (profileData.instagram_handle) profileScore += 4;
          if (profileData.phone) profileScore += 3;
          if (profileData.contact_name) profileScore += 2;
        }
        score += Math.min(profileScore, 25);
        
        // 2. Product Quantity & Activity (30 points max)
        let productQuantityScore = 0;
        if (dashboardData?.stats) {
          const stats = dashboardData.stats;
          const totalProducts = stats.total_products || 0;
          
          // Base points for having products
          if (totalProducts > 0) productQuantityScore += 10;
          if (totalProducts >= 3) productQuantityScore += 5;
          if (totalProducts >= 5) productQuantityScore += 5;
          if (totalProducts >= 10) productQuantityScore += 5;
          if (totalProducts >= 25) productQuantityScore += 5;
          
          // Recent activity bonus
          if (stats.products_this_month > 0) productQuantityScore += 5;
          if (stats.products_this_month >= 2) productQuantityScore += 5;
        }
        score += Math.min(productQuantityScore, 30);
        
        // 3. Product Quality (20 points max)
        let qualityScore = 0;
        if (dashboardData?.stats) {
          const stats = dashboardData.stats;
          const totalProducts = stats.total_products || 0;
          
          if (totalProducts > 0) {
            // Use backend quality score (0-100) and convert to 0-20 points
            const backendQuality = stats.product_quality_score || 0;
            qualityScore = (backendQuality / 100) * 20;
          }
        }
        score += Math.min(qualityScore, 20);
        
        // 4. Engagement & Performance (15 points max)
        let engagementScore = 0;
        if (dashboardData?.stats) {
          const stats = dashboardData.stats;
          const totalProducts = stats.total_products || 0;
          
          if (totalProducts > 0) {
            // Use backend engagement score (0-100) and convert to 0-15 points
            const backendEngagement = stats.engagement_score || 0;
            engagementScore = (backendEngagement / 100) * 15;
          }
        }
        score += Math.min(engagementScore, 15);
        
        // 5. Account Status (10 points max)
        let statusScore = 0;
        if (profileData) {
          if (profileData.status === 'active' || profileData.status === 'approved') {
            statusScore += 10;
          } else if (profileData.status === 'pending') {
            statusScore += 5;
          }
        }
        score += statusScore;
        
        // Ensure score is between 0 and 100
        const finalScore = Math.round(Math.min(Math.max(score, 0), 100));
        setBrandHealth(finalScore);
      } catch (err) {
        console.error('Failed to fetch brand data:', err);
      }
    };

    fetchBrandData();
  }, [isAuthenticated, authFetch]);

  const handleLogout = () => {
    logout();
    navigate('/brand-portal/login');
  };

  const navItems = [
    { path: '/brand-portal/dashboard', label: 'Overview', icon: LayoutDashboard },
    { path: '/brand-portal/collection', label: 'Collection', icon: Package },
    { path: '/brand-portal/analytics', label: 'Analytics', icon: TrendingUp },
    { path: '/brand-portal/insights', label: 'Insights', icon: Eye },
  ];

  // Documentation link (internal route)
  const documentationPath = '/brand-portal/docs';

  if (loading) {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.spinner} />
        <span style={{ color: '#71717a' }}>Loading...</span>
      </div>
    );
  }

  // Format brand name for display
  const brandName = profileData?.name || brand?.name || 'Brand Portal';
  const brandNameParts = brandName.split(' ');
  const brandLabel = brandNameParts[0]?.toUpperCase() || 'BRAND';

  return (
    <BrandStatusMonitor>
    <div className="brand-portal-isolated" style={styles.layout}>
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="bp-sidebar-overlay"
          style={{ display: 'block' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {/* Sidebar */}
      <aside className={`bp-sidebar ${sidebarOpen ? 'bp-sidebar-open' : ''}`} style={styles.sidebar}>
        {/* Mobile close button */}
        <button
          className="bp-sidebar-close"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close menu"
        >
          <X style={{ width: '20px', height: '20px' }} />
        </button>
        {/* Brand Header */}
        <div style={styles.sidebarHeader}>
          <div style={styles.logoContainer}>
            <Image 
              src={require('../../assets/images/newlogo.png')} 
              alt="Yemberzal" 
              style={styles.logoImage}
            />
          </div>
          <h2 style={{ ...styles.brandLabel, textAlign: 'center', fontSize: '18px' }}>{brandLabel}</h2>
          {/* <h2 style={styles.brandName}>{brandName}</h2> */}
        </div>
        
        {/* Navigation */}
        <nav style={styles.nav}>
          {navItems.map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/brand-portal/dashboard'}
              onClick={() => setSidebarOpen(false)}
              style={({ isActive }) => ({
                ...styles.navItem,
                ...(isActive ? styles.navItemActive : {})
              })}
            >
              <Icon style={{ width: '20px', height: '20px' }} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Brand Health Footer */}
        <div style={styles.sidebarFooter}>
          <div style={styles.healthLabel}>BRAND HEALTH</div>
          <div style={styles.healthValue}>{brandHealth}%</div>
          <div style={styles.healthStatus}>
            {brandHealth >= 80 ? 'Excellent' : 
             brandHealth >= 60 ? 'Good standing' : 
             brandHealth >= 40 ? 'Getting started' : 'Setup needed'}
          </div>
          
          {/* Account actions */}
          <div style={styles.accountSection}>
            <Link to="/brand-portal/profile" onClick={() => setSidebarOpen(false)} style={styles.accountLink}>
              <User style={{ width: '16px', height: '16px' }} />
              <span>Profile</span>
            </Link>
            <Link to={documentationPath} onClick={() => setSidebarOpen(false)} style={styles.accountLink}>
              <BookOpen style={{ width: '16px', height: '16px' }} />
              <span>Documentation</span>
            </Link>
            <button onClick={handleLogout} style={styles.logoutBtn}>
              <LogOut style={{ width: '16px', height: '16px' }} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="bp-main-wrapper" style={styles.mainWrapper}>
        {/* Top Header */}
        <header className="bp-top-header" style={styles.topHeader}>
          <div className="bp-header-content" style={styles.headerContent}>
            <div style={styles.headerLeft}>
              <button
                className="bp-hamburger"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label="Toggle menu"
              >
                <Menu style={{ width: '24px', height: '24px' }} />
              </button>
            </div>
            <div style={styles.headerRight}>
              {/* Notification Bell */}
              <div ref={notificationRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  style={styles.notificationBtn}
                  aria-label="Notifications"
                >
                  <Bell style={{ width: '20px', height: '20px' }} />
                  {unreadCount > 0 && (
                    <span style={styles.notificationBadge}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {showNotifications && (
                  <div className="bp-notification-dropdown" style={styles.notificationDropdown}>
                    <div style={styles.notificationHeader}>
                      <h3 style={styles.notificationTitle}>Notifications</h3>
                      {unreadCount > 0 && (
                        <button onClick={markAllAsRead} style={styles.markAllBtn}>
                          <Check style={{ width: '14px', height: '14px' }} />
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div style={styles.notificationList}>
                      {notifications.length === 0 ? (
                        <div style={styles.notificationEmpty}>
                          <Bell style={{ width: '32px', height: '32px', color: '#d4d4d4', marginBottom: '8px' }} />
                          <p style={{ margin: 0, color: '#71717a', fontSize: '14px' }}>No notifications yet</p>
                        </div>
                      ) : (
                        notifications.map((n) => (
                          <div
                            key={n.id}
                            style={{
                              ...styles.notificationItem,
                              background: n.is_read ? '#ffffff' : '#f5f5f5',
                            }}
                            onClick={() => !n.is_read && markAsRead(n.id)}
                          >
                            <div style={styles.notificationDot}>
                              {!n.is_read && <span style={{
                                width: '8px', height: '8px', borderRadius: '50%',
                                background: n.priority === 'high' ? '#ef4444' : n.priority === 'normal' ? '#3b82f6' : '#a1a1aa',
                                display: 'inline-block',
                              }} />}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={styles.notificationItemTitle}>{n.title}</div>
                              <div style={styles.notificationItemMessage}>{n.message}</div>
                              <div style={styles.notificationItemTime}>
                                {new Date(n.created_at).toLocaleDateString('en-US', {
                                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                })}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* User Avatar */}
              <div style={styles.userAvatar}>
                {(() => {
                  const name = profileData?.contact_name || brand?.name || 'Brand User';
                  const initials = name
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase())
                    .slice(0, 2)
                    .join('');
                  return initials;
                })()}
              </div>
            </div>
          </div>
        </header>
        <main className="bp-main" style={styles.main}>
          <Outlet />
        </main>

        {/* Portal Footer */}
        <footer className="bp-footer" style={styles.footer}>
          <div className="bp-footer-content" style={styles.footerContent}>
            <div style={styles.footerBrand}>
              <span style={styles.footerLogo}>Y</span>
              <span style={styles.footerName}>YEMBERZAL</span>
              <span style={styles.footerDivider}>|</span>
              <span style={styles.footerTagline}>Brand Portal</span>
            </div>
            <div style={styles.footerLinks}>
              <Link to="/" style={styles.footerLink}>Main Site</Link>
              <a href="mailto:yemberzal.me@gmail.com" style={styles.footerLink}>Support</a>
              <Link to="/brand-portal/profile" style={styles.footerLink}>Settings</Link>
            </div>
            <div style={styles.footerCopyright}>
              © {new Date().getFullYear()} Yemberzal. Kashmir-first fashion discovery.
            </div>
          </div>
        </footer>
      </div>
    </div>
    </BrandStatusMonitor>
  );
}

const styles = {
  layout: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
    background: '#ffffff',
  },
  loadingScreen: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    background: '#ffffff',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '2px solid #e5e5e5',
    borderTopColor: '#171717',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  sidebar: {
    width: '280px',
    minWidth: '280px',
    background: '#171717',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    position: 'fixed',
    top: 0,
    overflowY: 'auto',
    zIndex: 1001,
  },
  sidebarHeader: {
    padding: '32px 24px',
  },
  logoContainer: {
    width: '100%',
    height: '60px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoImage: {
    backgroundColor: 'transparent',
    background: 'none',
    width: '120%',
    height: '120%',
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
    transform: 'scale(1.2)',
  },
  brandLabel: {
    fontSize: '11px',
    letterSpacing: '0.2em',
    color: '#71717a',
    marginBottom: '4px',
  },
  brandName: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#ffffff',
    letterSpacing: '-0.02em',
  },
  nav: {
    padding: '16px',
    flex: 1,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    marginBottom: '4px',
    borderRadius: '8px',
    color: '#a1a1aa',
    textDecoration: 'none',
    fontSize: '15px',
    fontWeight: '500',
    transition: 'all 0.15s ease',
  },
  navItemActive: {
    background: '#ffffff',
    color: '#171717',
  },
  sidebarFooter: {
    padding: '24px',
    borderTop: '1px solid #262626',
  },
  healthLabel: {
    fontSize: '11px',
    letterSpacing: '0.15em',
    color: '#71717a',
    marginBottom: '8px',
  },
  healthValue: {
    fontSize: '32px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '4px',
  },
  healthStatus: {
    fontSize: '14px',
    color: '#a1a1aa',
    marginBottom: '20px',
  },
  accountSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingTop: '16px',
    borderTop: '1px solid #262626',
  },
  accountLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '6px',
    color: '#a1a1aa',
    textDecoration: 'none',
    fontSize: '13px',
    transition: 'all 0.15s',
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '6px',
    color: '#a1a1aa',
    background: 'transparent',
    border: 'none',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    width: '100%',
    textAlign: 'left',
  },
  mainWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '280px',
    height: '100vh',
    overflowY: 'auto',
  },
  topHeader: {
    background: '#ffffff',
    borderBottom: '1px solid #e5e5e5',
    padding: '0',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 68px',
    height: '64px',
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: '#171717',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
    letterSpacing: '0.5px',
  },
  notificationBtn: {
    position: 'relative',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'transparent',
    border: '1px solid #e5e5e5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#71717a',
    transition: 'all 0.2s',
  },
  notificationBadge: {
    position: 'absolute',
    top: '-2px',
    right: '-2px',
    minWidth: '18px',
    height: '18px',
    borderRadius: '9px',
    background: '#ef4444',
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
  },
  notificationDropdown: {
    position: 'absolute',
    top: '52px',
    right: 0,
    width: '380px',
    maxHeight: '480px',
    background: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e5e5e5',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    zIndex: 1000,
    overflow: 'hidden',
  },
  notificationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #e5e5e5',
  },
  notificationTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#171717',
    margin: 0,
  },
  markAllBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'transparent',
    border: 'none',
    color: '#3b82f6',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  notificationList: {
    maxHeight: '400px',
    overflowY: 'auto',
  },
  notificationEmpty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
  },
  notificationItem: {
    display: 'flex',
    gap: '12px',
    padding: '14px 20px',
    borderBottom: '1px solid #f5f5f5',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  notificationDot: {
    width: '12px',
    paddingTop: '6px',
    flexShrink: 0,
  },
  notificationItemTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#171717',
    marginBottom: '4px',
  },
  notificationItemMessage: {
    fontSize: '13px',
    color: '#52525b',
    lineHeight: 1.4,
    marginBottom: '4px',
  },
  notificationItemTime: {
    fontSize: '12px',
    color: '#a1a1aa',
  },
  main: {
    flex: 1,
    padding: '48px',
    background: '#ffffff',
  },
  footer: {
    background: '#fafafa',
    borderTop: '1px solid #e5e5e5',
    padding: '32px 48px',
  },
  footerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px',
  },
  footerBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  footerLogo: {
    width: '28px',
    height: '28px',
    background: '#171717',
    color: '#ffffff',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
  },
  footerName: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#171717',
    letterSpacing: '0.1em',
  },
  footerDivider: {
    color: '#d4d4d4',
    margin: '0 4px',
  },
  footerTagline: {
    fontSize: '13px',
    color: '#71717a',
  },
  footerLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  footerLink: {
    fontSize: '13px',
    color: '#71717a',
    textDecoration: 'none',
    transition: 'color 0.15s',
  },
  footerCopyright: {
    fontSize: '12px',
    color: '#a1a1aa',
    width: '100%',
    textAlign: 'center',
    paddingTop: '16px',
    borderTop: '1px solid #e5e5e5',
    marginTop: '16px',
  },
};

// Add keyframes for spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
if (!document.head.querySelector('style[data-bp-animations]')) {
  styleSheet.setAttribute('data-bp-animations', 'true');
  document.head.appendChild(styleSheet);
}

export default DashboardLayout;
